import { FeatureProvider } from "@calcom/features/flags/context/provider";
import { useFlags } from "@calcom/web/modules/feature-flags/hooks/useFlags";
import { PageTransitionLoader } from "@components/PageTransitionLoader";
import type { PageWrapperProps } from "@components/PageWrapperAppDir";
import { useNuqsParams } from "@lib/hooks/useNuqsParams";
import type { WithLocaleProps } from "@lib/withLocale";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import type { AppProps as NextAppProps } from "next/app";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useSearchParams } from "next/navigation";
import type { Session } from "next-auth";
import { NuqsAdapter } from "nuqs/adapters/next/app";

// Workaround for https://github.com/vercel/next.js/issues/8592
type AppProps = Omit<
  NextAppProps<
    WithLocaleProps<{
      nonce: string | undefined;
      themeBasis?: string;
      session: Session;
    }>
  >,
  "Component"
> & {
  Component: NextAppProps["Component"] & {
    requiresLicense?: boolean;
    isBookingPage?: boolean | ((arg: { router: NextAppProps["router"] }) => boolean);
    PageWrapper?: (props: AppProps) => JSX.Element;
  };

  /** Will be defined only is there was an error */
  err?: Error;
};

const getEmbedNamespace = (searchParams: ReadonlyURLSearchParams): string | null => {
  // Mostly embed query param should be available on server. Use that there.
  // Use the most reliable detection on client
  if (typeof window !== "undefined") {
    return window.getEmbedNamespace();
  }

  return searchParams.get("embed") ?? null;
};

type CalcomThemeProps = Readonly<{
  children: React.ReactNode;
}>;

const CalcomThemeProvider = (props: CalcomThemeProps): React.ReactNode => {
  const searchParams = useSearchParams();
  let embedNamespace: string | null = null;
  if (searchParams) {
    embedNamespace = getEmbedNamespace(searchParams);
  }
  const isEmbedMode = typeof embedNamespace === "string";

  return (
    <>
      {/* Embed Mode can be detected reliably only on client side here as there can be static generated pages as well which can't determine if it's embed mode at backend */}
      {/* color-scheme makes background:transparent not work in iframe which is required by embed. */}
      {typeof window !== "undefined" && !isEmbedMode && (
        //eslint-disable-next-line react/no-unknown-property
        <style jsx global>
          {`
            .dark {
              color-scheme: dark;
            }
          `}
        </style>
      )}
      {props.children}
    </>
  );
};

function FeatureFlagsProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const flags = useFlags();
  return <FeatureProvider value={flags}>{children}</FeatureProvider>;
}

function OrgBrandProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  return <>{children}</>;
}

const AppProviders = (props: PageWrapperProps): React.ReactNode => {
  const nuqsParams = useNuqsParams();

  const RemainingProviders = (
    <>
      <TooltipProvider>
        <PageTransitionLoader />
        {/* color-scheme makes background:transparent not work which is required by embed. We need to ensure next-theme adds color-scheme to `body` instead of `html`(https://github.com/pacocoursey/next-themes/blob/main/src/index.tsx#L74). Once that's done we can enable color-scheme support */}
        <CalcomThemeProvider>
          <NuqsAdapter {...nuqsParams}>
            <FeatureFlagsProvider>
              <OrgBrandProvider>{props.children}</OrgBrandProvider>
            </FeatureFlagsProvider>
          </NuqsAdapter>
        </CalcomThemeProvider>
      </TooltipProvider>
    </>
  );

  return RemainingProviders;
};

export default AppProviders;
export type { AppProps };
